'use client';
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { hashString, applyCalculatedHeight } from '../../../services/lib/util';
import parse, { domToReact } from 'html-react-parser';

export const TableOfContents = ({ tableOfContentsData, title }) => {
  const scrollToHeader = (id) => {
    const header = document.getElementById(id);
    if (header) {
      window.scrollTo({
        top: header.offsetTop + 76,
        behavior: 'smooth',
      });
    }
  };

  /**
   * Domyślnie rozwinięty, aby prawidłowo zmierzyć wysokość DOM.
   */
  const [isExpanded, setIsExpanded] = useState(true);

  const boxRef = useRef(null);
  const parentRef = useRef(null);

  useEffect(() => {
    const itemsToShow = 6;

    if (boxRef.current) {
      const listToMeasure = '.tableOfContents';
      const parentToSet = '.tableOfContentsParent';
      const itemsCount = applyCalculatedHeight(parentToSet, listToMeasure);
      if (itemsCount > itemsToShow) {
        setIsExpanded(false);
      } else {
        setIsExpanded(true);
      }
    }
  }, []);

  if (!tableOfContentsData || !tableOfContentsData.length) {
    return null;
  }

  return (
    <div
      ref={boxRef}
      className="table-of-contents-wrapper my-[30px] rounded-[8px] bg-[#242424] py-6 !px-5 px-0 text-white"
    >
      {title && <span className="block text-[1.375rem] font-semibold leading-[1.625rem] text-white px-0">{title}</span>}

      <div
        ref={parentRef}
        style={{ height: isExpanded ? 'auto' : '250px' }}
        className="tableOfContentsParent relative overflow-hidden transition-[height] duration-500 ease-in-out px-0"
      >
        <ul className="tableOfContents m-0 mt-[10px] flex flex-col gap-6 list-none p-0 py-3 px-0">
          {tableOfContentsData.map((item, index) => {
            const renderedHtml = item?.renderedHtml;
            if (!renderedHtml) {
              return null;
            }

            let extractedId = null;

            const parsedTocLabel = parse(renderedHtml, {
              replace: (domNode) => {
                if (domNode.type === 'tag' && /^h[1-6]$/.test(domNode.name)) {
                  if (domNode.attribs?.id) {
                    extractedId = domNode.attribs.id;
                  }

                  return (
                    <>
                      {domToReact(domNode.children, {
                        replace: (innerNode) => {
                          if (innerNode.type === 'text') {
                            return innerNode.data.replace(/:+$/, '');
                          }
                          if (innerNode.name === 'a' || innerNode.name === 'strong') {
                            return (
                              <>
                                {domToReact(innerNode.children, {
                                  replace: (deepNode) => {
                                    if (deepNode.type === 'text') {
                                      return deepNode.data.replace(/:+$/, '');
                                    }
                                  },
                                })}
                              </>
                            );
                          }
                        },
                      })}
                    </>
                  );
                }
              },
            });

            const id = extractedId || hashString(renderedHtml);

            return (
              <li
                key={index}
                onClick={() => scrollToHeader(id)}
                className="cursor-pointer text-[1rem] font-semibold leading-[1.25rem] text-primary transition-opacity hover:opacity-80 p-0 px-0"
              >
                {parsedTocLabel}
              </li>
            );
          })}
        </ul>

        {/* Gradient zanikania */}
        <div
          className={clsx(
            'fade-styles pointer-events-none absolute left-0 z-[1] w-full transition-opacity duration-500 ease-in-out px-0',
            'bottom-[-10px] h-[260px] sm:bottom-[-40px]',
            'bg-gradient-to-b from-[#242424]/0 via-[#242424]/0 to-[#242424]',
            isExpanded ? 'opacity-0' : 'opacity-100',
          )}
        />
      </div>

      {!isExpanded && (
        <div className="mt-6 flex justify-center px-0">
          <button
            type="button"
            onClick={() => {
              if (parentRef.current) {
                parentRef.current.style.height = 'auto';
              }
              setIsExpanded(true);
            }}
            className="min-w-[160px] cursor-pointer rounded-[4px] border-none bg-[#333333]/72 py-1.5 !px-3 px-0 font-medium normal-case text-white transition-colors hover:bg-[#444444] focus:outline-none"
          >
            Pokaż więcej
          </button>
        </div>
      )}
    </div>
  );
};
